import { orderSetStepMemberId } from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import type { useBookedOrderSetProposal } from "@/features/cases/hooks/use-booked-order-set-proposal";
import type { PresentedOrderSetDto } from "@/lib/order-set-presentation";
import { OrderSetFooterStatus } from "./order-set-footer-status";

type ProposalState = ReturnType<typeof useBookedOrderSetProposal>;
/** Times only: every window in this bar falls on the visit's own day, which the page already names. */
const clock = (at: string) => new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const windowLabel = (window: { start: string; end: string }) => `${clock(window.start)} – ${clock(window.end)}`;

/**
 * The commit bar for a booked order set: one sentence saying where the proposal
 * stands, and the two actions that end it. Submit has four ways to be
 * unavailable, so the sentence is what says which — a greyed button beside an
 * unrelated verdict left the reader to correlate them.
 */
export function OrderSetBookingFeedback({
  proposal,
  orderSet,
}: {
  proposal: ProposalState;
  orderSet: PresentedOrderSetDto;
}) {
  const stepLabel = (id?: string) => {
    if (!id) return "Whole visit";
    const index = orderSet.steps.findIndex((step) => orderSetStepMemberId(step.stepId) === id);
    return index < 0 ? id : `Step ${index + 1}`;
  };
  const conflicts = proposal.check.status === "checked" ? proposal.check.check.conflicts : [];
  const bookable = proposal.check.status === "checked" && proposal.check.check.bookable;
  /**
   * What is news about a conflict, and nothing else. The resource's id is left
   * out — it is a uuid at this width, and the type is what identifies it to a
   * reader; so is the conflict's own window, which is the step's, already drawn.
   */
  const conflictLine = (conflict: (typeof conflicts)[number]) => {
    const where = `${stepLabel(conflict.stepId)} ${conflict.resource.type}`;
    if (conflict.collision.kind === "busy") return `${where} busy ${windowLabel(conflict.collision.window)}`;
    if (conflict.collision.kind === "proposal") return `${where} overlaps ${stepLabel(conflict.collision.stepId)}`;
    return `${stepLabel(conflict.stepId)} has no eligible ${conflict.resource.type}`;
  };
  const status = (): Parameters<typeof OrderSetFooterStatus>[0] => {
    if (proposal.submitting) return { label: "Submitting", tone: "progress" };
    if (proposal.check.status === "error") return { label: "Error", tone: "error", message: proposal.check.message };
    if (proposal.check.status !== "checked") return { label: "Checking", tone: "progress" };
    if (proposal.check.check.bookable)
      return { label: "Bookable", tone: "success", message: windowLabel(proposal.check.check.window) };
    const only = conflicts.length === 1 ? conflicts[0] : undefined;
    return {
      label: "Not bookable",
      tone: "error",
      message: only ? conflictLine(only) : `${conflicts.length} conflicts`,
    };
  };
  return (
    <section aria-label="Booking proposal" className="flex items-center justify-between gap-2">
      <div className="min-w-0 flex-1">
        {(proposal.dirty || proposal.submitting) && <OrderSetFooterStatus {...status()} />}
      </div>
      <div className="flex shrink-0 gap-2">
        {proposal.check.status === "error" && (
          <Button type="button" size="sm" variant="outline" onClick={proposal.recheck}>
            Check again
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={!proposal.dirty || proposal.submitting}
          onClick={proposal.discard}
        >
          Discard
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!proposal.dirty || proposal.submitting || !bookable}
          onClick={proposal.submit}
        >
          Submit
        </Button>
      </div>
    </section>
  );
}
